/**
 * useComboCard - laedt die Daten fuer die Slide-out-Infokarte.
 * Aufruf bei Klick auf eine Matrix-Zelle (Substanz A x B):
 *   const { data, loading, error } = useComboCard(aId, bId);
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type {
  ComboCardData,
  SubstanceDetail,
  InteractionDetail,
} from "@/types/restlicht-db";

const SUBSTANCE_SELECT = `
  id, name,
  substance_profiles ( substance_id, summary, summary_en, dose_note, dose_note_en, general_notes, general_notes_en, tolerance_notes, tolerance_notes_en, risk_notes, risk_notes_en, harm_reduction, harm_reduction_en, confidence, sources, published, reviewed_at, reviewed_by, created_at, updated_at ),
  substance_routes ( id, substance_id, route, bioavailability_pct, onset_min_minutes, onset_max_minutes, duration_min_minutes, duration_max_minutes, after_effects_min_minutes, after_effects_max_minutes, notes, notes_en, sort_order, created_at, updated_at,
    substance_doses ( id, route_id, tier, amount_min, amount_max, unit, notes, notes_en, created_at, updated_at )
  ),
  substance_effects ( id, substance_id, kind, text, text_en, sort_order, created_at )
`;

interface State {
  data: ComboCardData | null;
  loading: boolean;
  error: string | null;
}

export function useComboCard(aId: string | null, bId: string | null): State {
  const [state, setState] = useState<State>({ data: null, loading: false, error: null });

  useEffect(() => {
    if (!aId || !bId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    (async () => {
      try {
        const [subsRes, comboRes] = await Promise.all([
          (supabase as unknown as {
            from: (t: string) => {
              select: (q: string) => { in: (c: string, v: string[]) => Promise<{ data: unknown; error: unknown }> };
            };
          })
            .from("substances")
            .select(SUBSTANCE_SELECT)
            .in("id", [aId, bId]),
          (supabase as unknown as {
            rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
          }).rpc("interaction_for_pair", { p_a: aId, p_b: bId }),
        ]);

        if (subsRes.error) throw subsRes.error;
        if (comboRes.error) throw comboRes.error;

        const rows = (subsRes.data ?? []) as SubstanceDetail[];
        const a = rows.find((r) => r.id === aId) ?? null;
        const b = rows.find((r) => r.id === bId) ?? null;

        if (!a || !b) throw new Error("Substanz nicht gefunden");

        const TIER_ORDER = { threshold: 0, light: 1, common: 2, strong: 3, heavy: 4 } as const;
        for (const s of [a, b]) {
          s.substance_routes?.sort((x, y) => x.sort_order - y.sort_order);
          s.substance_effects?.sort((x, y) => x.sort_order - y.sort_order);
          s.substance_routes?.forEach((r) => {
            r.substance_doses?.sort((x, y) => (TIER_ORDER[x.tier] ?? 99) - (TIER_ORDER[y.tier] ?? 99));
          });
        }

        // The RPC can answer with a row, with an array of rows or with an empty
        // array. An empty array means: no assessment exists for this pair.
        // Without this normalisation the empty array stays truthy and the pair
        // is rendered as an assessment without a risk level.
        const raw = comboRes.data ?? null;
        const interaction = (Array.isArray(raw) ? raw[0] ?? null : raw) as InteractionDetail | null;

        if (!cancelled) {
          setState({ data: { a, b, interaction }, loading: false, error: null });
        }
      } catch (e) {
        if (!cancelled) {
          setState({ data: null, loading: false, error: e instanceof Error ? e.message : String(e) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aId, bId]);

  return state;
}
